import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronRight } from "lucide-react";
import { LOGO_URL, CONTACT } from "@/data/site";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/service-areas", label: "Areas" },
  { to: "/pest-guide", label: "Pest Guide" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl backdrop-saturate-150 border-b border-black/5 shadow-md" : "bg-white/70 backdrop-blur-md backdrop-saturate-150 border-b border-white/30"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <img src={LOGO_URL} alt="EXORA Pest Control" className="h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-3 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                pathname === l.to ? "text-brand-green bg-brand-green/5" : "text-brand-charcoal/70 hover:text-brand-green"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <a href={`tel:${CONTACT.phoneRaw}`} data-testid="nav-phone"
             className="hidden 2xl:flex items-center gap-2 text-sm font-semibold text-brand-green">
            <Phone className="h-4 w-4" /> {CONTACT.phone}
          </a>
          {user ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" data-testid="nav-portal-btn"
                onClick={() => navigate(user.role === "admin" ? "/admin" : "/portal")}
                className="rounded-full text-brand-green font-semibold">
                {user.role === "admin" ? "Dashboard" : "My Portal"}
              </Button>
              <Button variant="outline" data-testid="nav-logout-btn" onClick={logout} className="rounded-full">
                Logout
              </Button>
            </div>
          ) : (
            <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")}
              className="rounded-full text-brand-green font-semibold">Sign In</Button>
          )}
          <Button data-testid="nav-inspection-btn" onClick={() => navigate("/request-inspection")}
            className="rounded-full bg-brand-gold text-brand-green font-bold hover:bg-brand-goldHover shadow-md">
            Free Inspection <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        <button data-testid="nav-mobile-toggle" className="xl:hidden text-brand-green p-2" onClick={() => setOpen(!open)}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-black/5 shadow-lg px-4 py-4 space-y-1">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="block px-4 py-3 rounded-lg text-brand-charcoal font-medium hover:bg-brand-green/5">
              {l.label}
            </Link>
          ))}
          <div className="pt-3 space-y-2">
            <Button data-testid="mobile-inspection-btn" onClick={() => navigate("/request-inspection")}
              className="w-full rounded-full bg-brand-gold text-brand-green font-bold">Free Inspection</Button>
            {user ? (
              <Button variant="outline" onClick={() => navigate(user.role === "admin" ? "/admin" : "/portal")}
                className="w-full rounded-full">{user.role === "admin" ? "Dashboard" : "My Portal"}</Button>
            ) : (
              <Button variant="outline" onClick={() => navigate("/login")} className="w-full rounded-full">Sign In</Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
